import React, { Component } from 'react';
import PropTypes from 'prop-types';
import withFetching from './WithFetching';

import HtmlViewer from './viewers/HtmlViewer';
import CsvViewer from './viewers/CsvViewer';
import TextViewer from './viewers/TextViewer';
import PhotoViewer from './viewers/PhotoViewer';
import VideoViewer from './viewers/VideoViewer';
import UnsupportedViewer from './viewers/UnsupportedViewer';
import EidViewer from './viewers/EidViewer';
import OutlookMsgViewer from './viewers/OutlookMsgViewer';
import EmlViewer from './viewers/EmlViewer';
import AudioViewer from './viewers/AudioViewer';
import EmbeddableDocumentViewer from './viewers/EmbeddableDocumentViewer';

const getExtensionFromName = fileName => {
  if (!fileName) return null;
  const lastDot = fileName.lastIndexOf('.');
  if (lastDot < 0) return null;
  return fileName.substring(lastDot + 1);
};

const normalEmbeddedMimeTypes = [
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
  'application/vnd.ms-excel.sheet.macroenabled.12',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
];

class FileViewer extends Component {
  state = {};

  constructor(props) {
    super(props);
    this.currentMimeType = props.mimeType;
  }
  shouldComponentUpdate(nextProps) {
    // Detect if viewer should render
    const {
      documentType: oldDocumentType,
      mimeType: oldMimeType,
      filePath: oldFilePath,
      fileName: oldFileName,
    } = this.props;
    const {
      documentType: newDocumentType,
      mimeType: newMimeType,
      filePath: newFilePath,
      fileName: newFileName,
    } = nextProps;

    if ((oldDocumentType === 10 || newDocumentType === 10) && oldDocumentType !== newDocumentType)
      return true;

    if (oldFilePath !== newFilePath) return true;
    if (oldMimeType !== newMimeType) return true;
    const oldExtension = getExtensionFromName(oldFileName);
    const newExtension = getExtensionFromName(newFileName);
    if (oldExtension !== newExtension) return true;

    return false;
  }

  componentDidMount() {
    const container = document.getElementById('pg-viewer');
    const height = container ? container.clientHeight : 0;
    const width = container ? container.clientWidth : 0;
    this.setState({ height, width });
  }

  getDriver() {
    const { documentType, mimeType, filePath, fileName, storageType } = this.props;
    const isEmbeddableMimeType = normalEmbeddedMimeTypes.indexOf(mimeType) > -1;

    if (filePath === null) {
      return UnsupportedViewer;
    }
    const extension = getExtensionFromName(fileName);

    // Render viewers based on custom document types first, such as eid
    if (documentType === 10 && extension === 'eid') {
      return withFetching(EidViewer);
    }

    if (extension && extension === 'msg') {
      return OutlookMsgViewer;
    }
    if (extension && extension === 'eml') {
      return EmlViewer;
    }

    // do this to avoid re-triggering fetch calls when the mime type doesn't change in the parent component due to state changes there.
    if (this.Driver && this.currentMimeType === mimeType) {
      return this.Driver;
    }

    if (isEmbeddableMimeType) {
      return EmbeddableDocumentViewer;
    }

    this.currentMimeType = mimeType;

    switch (this.currentMimeType.toLowerCase()) {
      case 'text/csv':
        return withFetching(CsvViewer);
      case 'text/html':
        return HtmlViewer;
      case 'image/jpg':
      case 'image/jpeg':
      case 'image/png':
      case 'image/bmp':
      case 'image/gif':
        return PhotoViewer;
      case 'application/xml':
      case 'text/plain':
        return withFetching(TextViewer);
      case 'video/webm':
      case 'video/mp4':
        return VideoViewer;
      case 'audio/wav':
      case 'audio/mpeg':
      case 'audio/mp4':
        return AudioViewer;
      default:
        return UnsupportedViewer;
    }
  }

  render() {
    const { width, height } = this.state;

    this.Driver = this.getDriver();

    return this.Driver && <this.Driver {...this.props} width={width} height={height} />;
  }
}

FileViewer.propTypes = {
  mimeType: PropTypes.string.isRequired,
  filePath: PropTypes.string.isRequired, // Download path
  fileName: PropTypes.string.isRequired, // File name
  unsupportedComponent: PropTypes.element,
  storageType: PropTypes.number.isRequired,
};

FileViewer.defaultProps = {
  unsupportedComponent: null,
};

export default FileViewer;
